/**
 * AI Service - unified interface for making AI provider calls.
 * All requests go through a Vercel Edge Function proxy to avoid CORS issues.
 */

import type { AIProvider, AIMessage, AICompletionOptions } from '../types'

// Configuration
const AI_CONFIG = {
  proxyEndpoint: '/api/ai/proxy',
  timeoutMs: 60000, // 60 seconds
  maxRetries: 2,
  retryDelayMs: 1000,
  retryableStatuses: [429, 500, 502, 503, 504],
}

// Message utilities
function extractSystemMessage(messages: AIMessage[]): {
  systemMessage: string | undefined
  messages: AIMessage[]
} {
  const systemMsg = messages.find(m => m.role === 'system')
  const otherMsgs = messages.filter(m => m.role !== 'system')
  return { systemMessage: systemMsg?.content, messages: otherMsgs }
}

// Provider definitions
interface ProviderConfig {
  name: string
  endpoint: string
  defaultModel: string
  authHeader: (key: string) => Record<string, string>
  formatRequest: (messages: AIMessage[], model: string) => Record<string, unknown>
  parseResponse: (response: Record<string, unknown>) => string
}

const PROVIDERS: Record<AIProvider, ProviderConfig> = {
  openai: {
    name: 'OpenAI',
    endpoint: 'https://api.openai.com/v1/chat/completions',
    defaultModel: 'gpt-4o',
    authHeader: (key) => ({ Authorization: `Bearer ${key}` }),
    formatRequest: (messages, model) => ({
      model,
      messages, // OpenAI accepts system role in messages array
    }),
    parseResponse: (response) => {
      const choices = response.choices as Array<{ message: { content: string } }> | undefined
      return choices?.[0]?.message?.content ?? ''
    },
  },

  anthropic: {
    name: 'Anthropic',
    endpoint: 'https://api.anthropic.com/v1/messages',
    defaultModel: 'claude-3-5-sonnet-20241022',
    authHeader: (key) => ({
      'x-api-key': key,
      'anthropic-version': '2023-06-01',
    }),
    formatRequest: (messages, model) => {
      // Anthropic requires system as a top-level parameter
      const { systemMessage, messages: chatMessages } = extractSystemMessage(messages)
      return {
        model,
        max_tokens: 4096,
        ...(systemMessage && { system: systemMessage }),
        messages: chatMessages,
      }
    },
    parseResponse: (response) => {
      const content = response.content as Array<{ text: string }> | undefined
      return content?.[0]?.text ?? ''
    },
  },

  gemini: {
    name: 'Gemini',
    endpoint: 'https://generativelanguage.googleapis.com/v1beta/models',
    defaultModel: 'gemini-1.5-flash',
    authHeader: (key) => ({ 'x-goog-api-key': key }),
    formatRequest: (messages, model) => {
      // Gemini uses systemInstruction for system prompts, 'model' role for assistant
      const { systemMessage, messages: chatMessages } = extractSystemMessage(messages)
      return {
        model, // Used by proxy to construct URL
        ...(systemMessage && {
          systemInstruction: { parts: [{ text: systemMessage }] },
        }),
        contents: chatMessages.map(m => ({
          role: m.role === 'assistant' ? 'model' : 'user',
          parts: [{ text: m.content }],
        })),
      }
    },
    parseResponse: (response) => {
      const candidates = response.candidates as Array<{
        content: { parts: Array<{ text: string }> }
      }> | undefined
      return candidates?.[0]?.content?.parts?.[0]?.text ?? ''
    },
  },

  openrouter: {
    name: 'OpenRouter',
    endpoint: 'https://openrouter.ai/api/v1/chat/completions',
    defaultModel: 'openrouter/auto',
    authHeader: (key) => ({
      Authorization: `Bearer ${key}`,
      'HTTP-Referer': 'https://praxiscrm.app',
      'X-Title': 'Praxis CRM',
    }),
    formatRequest: (messages, model) => ({
      model,
      messages, // OpenRouter follows OpenAI format
    }),
    parseResponse: (response) => {
      const choices = response.choices as Array<{ message: { content: string } }> | undefined
      return choices?.[0]?.message?.content ?? ''
    },
  },
}

// Custom error class for AI requests
export class AIError extends Error {
  status?: number
  retryable: boolean
  details?: Record<string, unknown>

  constructor(
    message: string,
    options?: { status?: number; retryable?: boolean; details?: Record<string, unknown> }
  ) {
    super(message)
    this.name = 'AIError'
    this.status = options?.status
    this.retryable = options?.retryable ?? false
    this.details = options?.details
  }
}

/**
 * Get a user-friendly error message from an AI error.
 */
export function getAIErrorMessage(error: Error): string {
  if (error instanceof AIError) {
    if (error.message.includes('timed out')) {
      return 'Request timed out. The AI provider may be slow or unavailable.'
    }
    if (error.status === 429) {
      return 'Rate limit exceeded. Please wait a moment and try again.'
    }
    if (error.status === 401 || error.status === 403) {
      return 'API key is invalid or expired. Please update it in Settings.'
    }
    if (error.status && error.status >= 500) {
      return 'AI provider is experiencing issues. Please try again later.'
    }
  }
  return 'AI request failed. Please try again.'
}

/**
 * AI Service class - handles making requests to AI providers.
 */
export class AIService {
  private providerId: AIProvider
  private provider: ProviderConfig
  private apiKey: string

  constructor(providerId: AIProvider, apiKey: string) {
    this.providerId = providerId
    this.provider = PROVIDERS[providerId]
    this.apiKey = apiKey

    if (!this.provider) {
      throw new Error(`Unknown provider: ${providerId}`)
    }
  }

  /**
   * Make a completion request to the AI provider.
   */
  async complete(messages: AIMessage[], options: AICompletionOptions = {}): Promise<string> {
    const model = options.model || this.provider.defaultModel
    const timeout = options.timeoutMs || AI_CONFIG.timeoutMs
    const maxRetries = options.maxRetries ?? AI_CONFIG.maxRetries

    const requestBody = this.provider.formatRequest(messages, model)

    let lastError: Error | undefined
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await this._makeRequest(requestBody, timeout)
      } catch (error) {
        lastError = error as Error

        // Don't retry on non-retryable errors
        if (error instanceof AIError && !error.retryable) {
          throw error
        }

        // Don't delay after final attempt
        if (attempt < maxRetries) {
          await this._delay(AI_CONFIG.retryDelayMs * Math.pow(2, attempt))
        }
      }
    }

    throw lastError
  }

  private async _makeRequest(
    requestBody: Record<string, unknown>,
    timeoutMs: number
  ): Promise<string> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

    try {
      const response = await fetch(AI_CONFIG.proxyEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: this.providerId,
          endpoint: this.provider.endpoint,
          headers: this.provider.authHeader(this.apiKey),
          body: requestBody,
        }),
        signal: controller.signal,
      })

      if (!response.ok) {
        let details: Record<string, unknown> | undefined
        let errorMessage = `AI request failed: ${response.status}`
        
        try {
          details = await response.json()
          // Extract provider-specific error messages
          const err = details?.error as Record<string, unknown> | string | unknown[] | undefined
          const providerError = 
            (typeof err === 'object' && err !== null && !Array.isArray(err) && 'message' in err ? err.message : null) ||
            (typeof err === 'object' && err !== null && !Array.isArray(err) && 'error' in err ? (err.error as Record<string, unknown>)?.message : null) ||
            details?.message ||
            (Array.isArray(err) && err[0] && typeof err[0] === 'object' && 'message' in err[0] ? (err[0] as Record<string, unknown>).message : null) ||
            (typeof err === 'string' ? err : null)
          
          if (providerError) {
            errorMessage = String(providerError)
          }
        } catch {
          // Ignore parse errors
        }

        throw new AIError(errorMessage, {
          status: response.status,
          retryable: AI_CONFIG.retryableStatuses.includes(response.status),
          details,
        })
      }

      const data = await response.json()
      return this.provider.parseResponse(data)
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw new AIError(`AI request timed out after ${timeoutMs}ms`, {
          retryable: true,
        })
      }
      throw error
    } finally {
      clearTimeout(timeoutId)
    }
  }

  private _delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }
}

/**
 * Validate an API key by making a minimal test request.
 */
export async function validateApiKey(
  provider: AIProvider,
  apiKey: string
): Promise<{ valid: boolean; warning?: string; error?: string }> {
  const service = new AIService(provider, apiKey)
  try {
    await service.complete([{ role: 'user', content: 'Hi' }], {
      timeoutMs: 15000,
      maxRetries: 0,
    })
    return { valid: true }
  } catch (error) {
    if (error instanceof AIError) {
      if (error.status === 401 || error.status === 403) {
        return { valid: false, error: 'Invalid API key' }
      }
      if (error.status === 429) {
        return { valid: true, warning: 'Key valid but rate limited' }
      }
      // Return the actual provider error message for debugging
      return { valid: false, error: error.message }
    }
    return { valid: false, error: error instanceof Error ? error.message : 'Validation failed' }
  }
}
